'use client';

import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { addToCartClientSideAndRevalidate } from 'services/cart/actions';
import {
  AddOrderItemRequest,
  AddOrderItemResponse
} from 'services/order/types';

const CoreContext = createContext<{
  isAddingToCart: boolean;
  isCartEdited: boolean;
  setIsAddingToCart: Dispatch<SetStateAction<boolean>>;
  handleAddToCart: (
    bodyRequest: AddOrderItemRequest,
    storeId: string,
    locale: string
  ) => Promise<{ error: boolean; response?: AddOrderItemResponse }>;
}>(null);

export const useCoreContext = () => useContext(CoreContext);

export const CoreProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isCartEdited, setIsCartEdited] = useState(false);
  const handleAddToCart = useCallback(
    async (
      bodyRequest: AddOrderItemRequest,
      storeId: string,
      locale: string
    ) => {
      setIsCartEdited(true);
      setIsAddingToCart(true);
      try {
        const res = await addToCartClientSideAndRevalidate(
          bodyRequest,
          storeId,
          locale
        );
        return res;
      } catch (error) {
        console.log(error);
        return { error: true };
      } finally {
        setIsAddingToCart(false);
      }
    },
    []
  );
  const coreContext = useMemo(
    () => ({
      isAddingToCart,
      handleAddToCart,
      setIsAddingToCart,
      isCartEdited
    }),
    [isAddingToCart, handleAddToCart, isCartEdited]
  );
  return (
    <CoreContext.Provider value={coreContext}>{children}</CoreContext.Provider>
  );
};
